import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useInfiniteConversations,
  useDeleteConversation,
  useUpdateConversation,
} from '../../hooks/useConversations';
import { useMeAgents as useAgents } from '../../hooks/useAgents';
import {
  Button,
  ConfirmModal,
  InlineEdit,
  Input,
  Table,
} from '@/components/ui';
import { useToast } from '../../components/ui/Toast';
import { routes } from '../../lib/routes';
import type { Conversation, Agent } from '../../types';
import { useConversationNamedEvent } from '../../hooks/useConversationNamedEvent';
import { cleanDisplayText } from '@/utils/display-text';

export default function ConversationList() {
  const navigate = useNavigate();
  const { success, error: showError } = useToast();
  const {
    items: conversations,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteConversations();
  const { data: agentsData } = useAgents();
  const deleteConversation = useDeleteConversation();
  const updateConversation = useUpdateConversation();

  useConversationNamedEvent();

  const [searchQuery, setSearchQuery] = useState('');
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    conversation: Conversation | null;
  }>({ isOpen: false, conversation: null });

  const agentMap = new Map(
    (agentsData?.items ?? []).map((a: Agent) => [a.agentId, a]),
  );

  const filteredConversations = conversations.filter((c) => {
    const q = searchQuery.toLowerCase();
    const title = (cleanDisplayText(c.name) || 'New Conversation').toLowerCase();
    const agentName = (agentMap.get(c.agentId ?? '')?.name ?? '').toLowerCase();
    return title.includes(q) || agentName.includes(q);
  });

  const handleDelete = async () => {
    if (!deleteModal.conversation) return;
    try {
      await deleteConversation.mutateAsync(deleteModal.conversation.conversationId);
      success('Conversation deleted');
      setDeleteModal({ isOpen: false, conversation: null });
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete conversation');
    }
  };

  const columns = [
    {
      key: 'title',
      header: 'Title',
      render: (c: Conversation) => (
        <div>
          <p className="font-medium text-foreground">
            <InlineEdit
              value={c.name || ''}
              placeholder="New Conversation"
              onSave={async (name) => {
                await updateConversation.mutateAsync({
                  conversationId: c.conversationId,
                  input: { name: name || undefined },
                });
              }}
            />
          </p>
          <p className="text-muted-foreground text-xs mt-0.5">
            {c.messageCount} message{c.messageCount !== 1 ? 's' : ''}
          </p>
        </div>
      ),
    },
    {
      key: 'agent',
      header: 'Agent',
      render: (c: Conversation) => {
        const agent = agentMap.get(c.agentId ?? '');
        return (
          <span className="text-sm text-foreground">
            {agent?.name ?? c.agentId ?? '-'}
          </span>
        );
      },
    },
    {
      key: 'updatedAt',
      header: 'Last Active',
      render: (c: Conversation) => (
        <span className="text-muted-foreground">
          {new Date(c.updatedAt).toLocaleDateString()}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (c: Conversation) => (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setDeleteModal({ isOpen: true, conversation: c });
            }}
            className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/30"
          >
            Delete
          </Button>
        </div>
      ),
      className: 'text-right',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Conversations</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Chat history with your agents
          </p>
        </div>
        <Button onClick={() => navigate(routes.operator.chatNew)}>New Chat</Button>
      </div>

      <div className="flex gap-4">
        <div className="flex-1 max-w-md">
          <Input
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e: { target: { value: string } }) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      <Table
        columns={columns}
        data={filteredConversations}
        keyExtractor={(c) => c.conversationId}
        onRowClick={(c) => navigate(routes.operator.chat(c.conversationId))}
        emptyMessage="No conversations yet. Start a new conversation to get going."
        isLoading={isLoading}
      />

      {hasNextPage && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage ? 'Loading…' : 'Show more'}
          </Button>
        </div>
      )}

      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, conversation: null })}
        onConfirm={handleDelete}
        title="Delete Conversation"
        message={`Are you sure you want to delete "${cleanDisplayText(deleteModal.conversation?.name) || 'New Conversation'}"? This action cannot be undone.`}
        confirmText="Delete"
        isLoading={deleteConversation.isPending}
      />
    </div>
  );
}
